import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bookmark, Trash2, ArrowRight, Newspaper, Copy, Check } from "lucide-react";
import { listSavedIdeas, deleteSavedIdea } from "@/lib/api";
import { TipoBadge } from "@/components/Ideas";
import { toast } from "sonner";

const CAT_COLOR = {
  Connessione: "#6B7280",
  "Tabù di coppia": "#D92D20",
  Sistema: "#D97706",
};

function CopyIcon({ text }) {
  const [c, setC] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text || ""); setC(true); toast.success("Hook copiato"); setTimeout(() => setC(false), 1500); }}
      className="text-neutral-500 hover:text-white transition-colors"
      title="Copia hook"
    >
      {c ? <Check size={15} /> : <Copy size={15} />}
    </button>
  );
}

export default function SavedIdeas({ onUseIdea, reloadKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listSavedIdeas());
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const onDelete = async (id) => {
    try {
      await deleteSavedIdea(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Rimossa dai preferiti");
    } catch {
      toast.error("Errore");
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Bookmark size={22} className="text-[#D92D20]" fill="#D92D20" />
        <h2 className="font-display font-extrabold text-3xl tracking-tight">Idee salvate</h2>
      </div>
      <p className="text-sm text-neutral-500 mb-6">Le idee che hai messo da parte. Sviluppale quando vuoi.</p>

      {loading ? (
        <p className="text-neutral-500">Carico...</p>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#262626] rounded-md py-20 flex flex-col items-center text-center">
          <Bookmark size={36} className="text-neutral-700 mb-3" />
          <p className="text-neutral-500 max-w-sm">
            Non hai ancora salvato idee. Nella sezione Idee premi l'icona segnalibro su quelle che ti piacciono di più.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="saved-ideas-list">
          <AnimatePresence>
            {items.map((idea, i) => (
              <motion.div
                key={idea.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-[#121212] border border-[#262626] rounded-md p-5 flex flex-col hover:border-[#333] transition-colors"
                data-testid={`saved-card-${i}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-[0.6rem] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm"
                      style={{ background: `${CAT_COLOR[idea.categoria] || "#6B7280"}22`, color: CAT_COLOR[idea.categoria] || "#9CA3AF" }}
                    >
                      {idea.categoria}
                    </span>
                    <TipoBadge tipo={idea.tipo} />
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <CopyIcon text={idea.hook_fissato} />
                    <button
                      data-testid={`saved-delete-${idea.id}`}
                      onClick={() => onDelete(idea.id)}
                      className="text-neutral-500 hover:text-[#D92D20] transition-colors"
                      title="Rimuovi"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                <p className="font-display font-extrabold text-lg leading-tight text-white mb-3">{idea.hook_fissato}</p>
                <p className="text-sm text-neutral-300 mb-2">{idea.angolo}</p>
                {idea.protagonista && (
                  <p className="text-xs text-neutral-500 mb-1"><span className="text-neutral-600">Protagonista:</span> {idea.protagonista}</p>
                )}
                {idea.spunto && (
                  <p className="text-[0.7rem] text-neutral-400 bg-[#1A1A1A] border border-[#262626] rounded-sm px-2.5 py-1.5 mb-4 flex items-start gap-1.5">
                    <Newspaper size={12} className="text-[#D97706] mt-0.5 shrink-0" />
                    <span><span className="text-neutral-500">Spunto:</span> {idea.spunto}</span>
                  </p>
                )}

                <button
                  data-testid={`saved-use-${i}`}
                  onClick={() => onUseIdea(idea)}
                  className="mt-auto self-start flex items-center gap-1.5 text-sm font-semibold text-[#D92D20] hover:gap-2.5 transition-all"
                >
                  Scrivi lo script <ArrowRight size={15} />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
